import { BlobClient } from '@frontend/blob/api';
import { EntityType } from '@frontend/common';
import { Logger } from '@frontend/logger';
import { TransactionAttachmentClient } from '@frontend/transaction/api';
import { TransactionAttachmentType } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { RefObject, useEffect, useRef, useState } from 'react';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

export interface TransactionAttachmentFile {
    id: string;
    name: string;
    size: number | null;
    type: string | null;
}

export type TransactionAttachmentError = 'load' | 'upload' | 'delete' | null;

interface ViewProps {
    trackingNumber: string | null;
    setTrackingNumber: (value: string | null) => void;
    shippingNotes: string | null;
    setShippingNotes: (value: string | null) => void;
    fileInputRef: RefObject<HTMLInputElement | null>;
    attachments: TransactionAttachmentFile[];
    isLoadingAttachments: boolean;
    isUploadingAttachments: boolean;
    removingAttachmentIds: Set<string>;
    attachmentError: TransactionAttachmentError;
    addAttachments: (files: File[]) => Promise<void>;
    removeAttachment: (attachmentId: string) => Promise<void>;
    submitDisabled: boolean;
    onSubmit: () => Promise<void>;
    onBack: (type: ScreenType) => void;
}

export const useEnterTrackingNumberAndShippingNotes = (props: { wizard_id: string }): ViewProps => {
    const { store, onBack, updateWizardData, onSubmit: navigateToNextStep } = useScreen(props.wizard_id);
    const { syncTransaction } = useWizardTransaction({ wizardId: props.wizard_id, store });
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [trackingNumber, setTrackingNumber] = useState<string | null>(store?.data.trackingNumber || store?.data.transaction?.tracking_number || null);
    const [shippingNotes, setShippingNotes] = useState<string | null>(store?.data.shippingNotes || store?.data.transaction?.shipping_notes || null);
    const [attachments, setAttachments] = useState<TransactionAttachmentFile[]>([]);
    const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
    const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
    const [removingAttachmentIds, setRemovingAttachmentIds] = useState<Set<string>>(new Set());
    const [attachmentError, setAttachmentError] = useState<TransactionAttachmentError>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const transaction = store?.data.transaction;
    const transactionId = transaction?.id;

    useEffect(() => {
        if (store?.data.trackingNumber) {
            setTrackingNumber(store.data.trackingNumber);
        } else if (store?.data.transaction?.tracking_number) {
            setTrackingNumber(store.data.transaction.tracking_number);
        }

        if (store?.data.shippingNotes) {
            setShippingNotes(store.data.shippingNotes);
        } else if (store?.data.transaction?.shipping_notes) {
            setShippingNotes(store.data.transaction.shipping_notes);
        }
    }, [store?.data.shippingNotes, store?.data.trackingNumber, store?.data.transaction?.shipping_notes, store?.data.transaction?.tracking_number]);

    useEffect(() => {
        if (!transactionId || !transaction?.account_id) {
            setAttachments([]);
            return;
        }

        let cancelled = false;
        setIsLoadingAttachments(true);
        setAttachmentError(null);

        TransactionAttachmentClient.fetchTransactionAttachments({ transaction_id: transactionId })
            .then(async (response) => {
                const files = await Promise.all(
                    response.results.map(async (attachment): Promise<TransactionAttachmentFile> => {
                        try {
                            const result = await BlobClient.fetchBlobWithFilename(transaction.account_id, EntityType.TRANSACTION_ATTACHMENT, attachment.id);
                            return {
                                id: attachment.id,
                                name: result.filename || attachment.id,
                                size: result.blob.size,
                                type: result.blob.type || null
                            };
                        } catch {
                            return {
                                id: attachment.id,
                                name: attachment.id,
                                size: null,
                                type: null
                            };
                        }
                    })
                );

                if (!cancelled) setAttachments(files);
            })
            .catch((error) => {
                Logger.error('Failed to load transaction attachments', error);
                if (!cancelled) {
                    setAttachments([]);
                    setAttachmentError('load');
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoadingAttachments(false);
            });

        return () => {
            cancelled = true;
        };
    }, [transaction?.account_id, transactionId]);

    const addAttachments = async (files: File[]) => {
        if (!store || files.length === 0 || isLoadingAttachments || isUploadingAttachments || removingAttachmentIds.size > 0) return;

        setAttachmentError(null);
        setIsUploadingAttachments(true);
        try {
            const activeTransaction = transaction?.id
                ? transaction
                : await syncTransaction({
                      tracking_number: trackingNumber,
                      shipping_notes: shippingNotes
                  });

            if (!activeTransaction?.id) return;

            for (const file of files) {
                let attachmentId: string | null = null;
                try {
                    const attachment = await TransactionAttachmentClient.postTransactionAttachment(activeTransaction.account_id, activeTransaction.id, {
                        type: TransactionAttachmentType.SHIPPING_NOTES
                    });
                    attachmentId = attachment.id;
                    await BlobClient.postBlob(activeTransaction.account_id, EntityType.TRANSACTION_ATTACHMENT, attachment.id, file);
                    setAttachments((current) => [
                        ...current,
                        {
                            id: attachment.id,
                            name: file.name,
                            size: file.size,
                            type: file.type || null
                        }
                    ]);
                } catch (error) {
                    Logger.error('Failed to upload transaction attachment', error);
                    setAttachmentError('upload');
                    if (attachmentId) {
                        await TransactionAttachmentClient.deleteTransactionAttachment(activeTransaction.account_id, activeTransaction.id, attachmentId).catch(
                            (rollbackError) => Logger.error('Failed to roll back transaction attachment', rollbackError)
                        );
                    }
                }
            }
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
            setIsUploadingAttachments(false);
        }
    };

    const removeAttachment = async (attachmentId: string) => {
        if (!transaction?.id || isUploadingAttachments || removingAttachmentIds.has(attachmentId)) return;

        setAttachmentError(null);
        setRemovingAttachmentIds((current) => new Set(current).add(attachmentId));
        try {
            await BlobClient.deleteBlob(transaction.account_id, EntityType.TRANSACTION_ATTACHMENT, attachmentId).catch((error: unknown) => {
                const isNotFound = typeof error === 'object' && error !== null && 'code' in error && error.code === 404;
                if (!isNotFound) throw error;
            });
            await TransactionAttachmentClient.deleteTransactionAttachment(transaction.account_id, transaction.id, attachmentId);
            setAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
        } catch (error) {
            Logger.error('Failed to delete transaction attachment', error);
            setAttachmentError('delete');
        } finally {
            setRemovingAttachmentIds((current) => {
                const updated = new Set(current);
                updated.delete(attachmentId);
                return updated;
            });
        }
    };

    const onSubmit = async () => {
        if (isSubmitting || isLoadingAttachments || isUploadingAttachments || removingAttachmentIds.size > 0 || !store) return;

        setIsSubmitting(true);
        try {
            const updatedTransaction = await syncTransaction({
                tracking_number: trackingNumber,
                shipping_notes: shippingNotes
            });

            updateWizardData({
                trackingNumber,
                shippingNotes,
                transaction: updatedTransaction
            });

            const nextStep = store.steps.find(
                (step) => step.screen === ScreenType.ENTER_TRACKING_NUMBER_AND_SHIPPING_NOTES && step.trigger === ScreenTrigger.SUBMIT
            );
            navigateToNextStep(nextStep);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        trackingNumber,
        setTrackingNumber,
        shippingNotes,
        setShippingNotes,
        fileInputRef,
        attachments,
        isLoadingAttachments,
        isUploadingAttachments,
        removingAttachmentIds,
        attachmentError,
        addAttachments,
        removeAttachment,
        submitDisabled: isSubmitting || isLoadingAttachments || isUploadingAttachments || removingAttachmentIds.size > 0,
        onSubmit,
        onBack
    };
};

export default useEnterTrackingNumberAndShippingNotes;
